import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  deriveAddressRecord,
  formatAddressSummary,
  mergeAddressRecords,
} from "../utils/addressHelpers";

export const useAddressFieldState = ({
  value,
  readOnly,
  displayKey,
  isControlled,
  controlledAddress,
  controlledInitial,
  controlledIsEditing,
  setControlledIsEditing,
  controlledHasAddress,
  isRequired,
  errorsProp,
  onChange,
  onAddressChange,
}) => {
  const derivedInitial = useMemo(
    () => (isControlled ? null : deriveAddressRecord(value, readOnly, displayKey)),
    [isControlled, value, readOnly, displayKey]
  );

  const [internalAddress, setInternalAddress] = useState(() => derivedInitial);
  const [internalIsEditing, setInternalIsEditing] = useState(() =>
    isControlled ? false : !derivedInitial
  );
  const internalInitialRef = useRef(isControlled ? null : derivedInitial);
  const skipDerivedSyncRef = useRef(false);

  useEffect(() => {
    if (isControlled) {
      return;
    }

    if (skipDerivedSyncRef.current) {
      skipDerivedSyncRef.current = false;
      return;
    }

    setInternalAddress((prev) => mergeAddressRecords(prev, derivedInitial));

    if (derivedInitial) {
      internalInitialRef.current = mergeAddressRecords(internalInitialRef.current, derivedInitial);
    } else {
      internalInitialRef.current = null;
    }

    setInternalIsEditing(!derivedInitial);
  }, [isControlled, derivedInitial]);

  const addressRecord = isControlled ? controlledAddress : internalAddress;
  const initialAddress = isControlled
    ? controlledInitial ?? controlledAddress ?? deriveAddressRecord(value, readOnly, displayKey)
    : internalInitialRef.current;
  const isEditing = isControlled ? controlledIsEditing ?? false : internalIsEditing;
  const setIsEditing = isControlled ? setControlledIsEditing ?? (() => {}) : setInternalIsEditing;

  const uncontrolledHasAddress = useMemo(
    () => Boolean(addressRecord?.id || value),
    [addressRecord, value]
  );

  const hasAddress = isControlled
    ? controlledHasAddress ??
      Boolean(addressRecord?.id || deriveAddressRecord(value, readOnly, displayKey))
    : uncontrolledHasAddress;

  const combinedErrors = useMemo(() => {
    if (errorsProp && Object.keys(errorsProp).length > 0) {
      return errorsProp;
    }
    if (!isControlled && isRequired && !hasAddress) {
      return { address: "This field is required." };
    }
    return errorsProp || {};
  }, [errorsProp, isControlled, isRequired, hasAddress]);

  const displayAddress = useMemo(
    () => formatAddressSummary(addressRecord, readOnly),
    [addressRecord, readOnly]
  );

  const showError = Boolean(
    (combinedErrors.address || combinedErrors.incident_city) && !hasAddress
  );

  const handleAddressSelect = useCallback(
    (address) => {
      if (isControlled) {
        onAddressChange?.(address);
        setIsEditing(false);
        return;
      }
      setInternalAddress(address ?? null);
      if (address?.id) {
        internalInitialRef.current = address;
      } else {
        internalInitialRef.current = null;
      }
      skipDerivedSyncRef.current = true;
      onChange?.(address?.id ?? null);
      setIsEditing(false);
    },
    [isControlled, onAddressChange, onChange, setIsEditing]
  );

  return {
    addressRecord,
    initialAddress,
    isEditing,
    setIsEditing,
    hasAddress,
    combinedErrors,
    showError,
    displayAddress,
    handleAddressSelect,
  };
};

export default useAddressFieldState;
